import { Component, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import { HttpErrorResponse} from "@angular/common/http";
import { IrbEditor} from "@irb/irb-editors/irb-editors.base";
import { IEvent, IrbEditorsEvents, IrbAppEvents, IrbDialogsEvents} from "@irb-common/events";
import { IAlertDialogData } from "@irb/app.model";
import { IDropdownItem } from '@irb-common/models/dropdown.model';
import { DropdownFilterComponent} from "@irb-common/components/dropdown-filter/dropdown-filter.component";
import { IrbOrdersService } from '@irb/irb-orders/irb-orders.service';
import { IOrders, IBidAndAskPrices } from "@irb/irb-orders/irb-orders.model";
import { EditorQuickOrderEntryGridLayoutService} from "@irb/irb-editors/editor-quick-order-entry/editor-quick-order-entry.grid-layout.service";
import { NonNullableFormBuilder } from '@angular/forms';

@Component({
  selector: 'irb-editor-quick-order-entry',
  standalone: false,
  templateUrl: './editor-quick-order-entry.component.html',
  styleUrl: './editor-quick-order-entry.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class EditorQuickOrderEntryComponent extends IrbEditor implements OnInit {
  isUiTest = false;
  trade: IOrders = {};
  tradeList: Array<IOrders> = [];
  isTradeValid: boolean = false; 
  isMutualFundOrder: boolean = false;
  isDollars: boolean = false;
  isSell: boolean = false;
  symbolPrices: IBidAndAskPrices;
  slideOutTitle: string = 'Quick Order Entry';
  tomorrowDate: string;
  daySixMonthsAfter: string;
  daySixMonthsAfterDisplay: string;
  isExpirationDateEnabled: boolean = false;
  expirationDatePlaceholder: string = 'n/a';
  grid: any;
  gridOptions: any;
  gridHeight: string = "240px";
  frameworkComponents: any;
  isSellAll: boolean = false;

  @ViewChild('accountNumberDropdown') accountNumberDropdown: any;
  accountNumberTarget: IDropdownItem | null = null;

  @ViewChild('accountNameDropdown') accountNameDropdown: any;
  accountNameTarget: IDropdownItem | null = null;

  @ViewChild('symbolDropdown') symbolDropdown: any;  
  symbolTarget: IDropdownItem | null = null;

  @ViewChild('transTypeDropdown') transTypeDropdown: any;
  transTypeTarget: IDropdownItem | null = null;
  transTypes: Array<IDropdownItem>;

  @ViewChild('denominationDropdown') denominationDropdown: any;
  denominationTarget: IDropdownItem | null = null;
  denominations: Array<IDropdownItem>;

  @ViewChild('orderTypeDropdown') orderTypeDropdown: any;
  orderTypeTarget: IDropdownItem | null = null;
  orderTypes: Array<IDropdownItem>;

  @ViewChild('priceTypeDropdown') priceTypeDropdown: any;
  priceTypeTarget: IDropdownItem | null = null;
  priceTypes: Array<IDropdownItem>;

  @ViewChild('expirationTypeDropdown') expirationTypeDropdown: any;
  expirationTypeTarget: IDropdownItem | null = null;
  expirationTypes: Array<IDropdownItem>;

  @ViewChild('quantity') quantityInput: any;
  @ViewChild('dollarAmount') dollarAmountInput: any;
  @ViewChild('price') priceInput: any;
  @ViewChild('limitPrice') limitPriceInput: any;
  @ViewChild('stopPrice') stopPriceInput: any;
  @ViewChild('expirationDate') expirationDate: any;

  constructor(
    private ors: IrbOrdersService, 
    private gls: EditorQuickOrderEntryGridLayoutService
  ) {
    super();
  }

  ngOnInit() {
    this.tomorrowDate = this.util.formatDateTimeString(this.util.incrementDays(new Date(), 1), "yyyy-MM-dd");
    const d6m = this.util.incrementDays(new Date(), 179);
    this.daySixMonthsAfter = this.util.formatDateTimeString(d6m, "yyyy-MM-dd");
    this.daySixMonthsAfterDisplay = this.util.formatDateTimeString(d6m, "MM/dd/yyyy");

    this.transTypes = [
      {name: "Buy", value: "Buy"},
      {name: "Sell", value: "Sell"},
      {name: "Sell All", value: "Sell All"}
    ];

    this.denominations = [
      {name: "Dollars", value: "Dollars"},
      {name: "Shares", value: "Shares"}
    ];

    this.orderTypes = [
      {name: "Market", value: "Market"},
      {name: "Limit", value: "Limit"},
      {name: "Stop", value: "Stop"},
      {name: "Stop Limit", value: "Stop Limit"}
    ];

    this.priceTypes = [
      {name: "Price", value: "None"},
      {name: "Last Trade", value: "LASTTRADE"},
      {name: "Bid", value: "BID"},
      {name: "Ask", value: "ASK"},
      {name: "Midpoint", value: "MIDPOINT"}
    ];

    this.expirationTypes = [
      {name: "Day", value: "DAY"},
      {name: "Day Ext", value: "DAY_EXT"},
      {name: "Ext. AM", value: "EXT_AM"},
      {name: "Ext. PM", value: "EXT_PM"},
      {name: "GT6m", value: "GT6M"},
      {name: "GTD", value: "GTD"},
      {name: "GTD Ext", value: "GTD_EXT"}
    ];

    this.gridOptions = {
      columnDefs: this.gls.getColumnDefs(this),      
      floatingFilter: false,
      overlayNoRowsTemplate: "<span class='ag-overlay-loading-center ag-overlay-message'>No trades.</span>",
      context: { componentParent: this }
    };

    this.frameworkComponents = this.gls.getFrameworkComponents();
    this.updateGridHeight();
  }

  onGridReady(params: any) {
    this.grid = params;
  }

  get(_event: IEvent) {
    this.load(this.tradeList);
  }

  setAccountNumberTarget(value: any) {
    this.accountNumberTarget = value;
    this.accountNameTarget = {
      name: this.accountNumberTarget?.value,
      value: this.accountNumberTarget?.name
    }
    this.trade.accountNumber = Number(this.accountNumberTarget?.name);
    this.trade.accountName = this.accountNumberTarget?.value;
    this.validateTrade();
  }

  getAccountNumbers(searchStr: any) {
    const sortByKey = this.util.sortByKey;

    this.ors.getAccountNumber(searchStr).subscribe((data: any) => {
      sortByKey(data, "name");
      this.accountNumberDropdown.set(data);
    });
  }

  setAccountNameTarget(value: any) {
    value.name = value.name.replace(new RegExp(`\\s${value.value}`), '');
    this.accountNameTarget = value;
    this.accountNumberTarget = {
      name: this.accountNameTarget?.value,
      value: this.accountNameTarget?.name
    }
    this.trade.accountName = this.accountNameTarget?.name;
    this.trade.accountNumber = Number(this.accountNameTarget?.value);
  }

  clearAccountTarget() {
    this.accountNumberTarget = null;
    this.accountNameTarget = null;
    this.trade.accountNumber = undefined;
    this.trade.accountName = undefined;
    this.validateTrade();
  }

  clearSymbolTarget() {
    this.symbolTarget = null;
    this.trade.securitySymbol = undefined;
    this.validateTrade();
  }

  getAccountNames(searchStr: any) {
    const sortByKey = this.util.sortByKey;

    this.ors.getAccountName(searchStr).subscribe((data: any) => {
      data = data.map((x: any) => {
        x.name = x.name + " " + x.value;
        return x;
      });

      sortByKey(data, "name");
      this.accountNameDropdown.set(data);
    });
  }

  setSymbolTarget(value: any) {
    this.ors.getSymbolPrices(value.name).subscribe((data: any) => {
      this.symbolTarget = value;
      this.trade.securitySymbol = this.symbolTarget?.name;

      this.symbolPrices = {
        "ask": data.ask,
        "midpoint": data.midpoint,
        "bid": data.bid,
        "lastPrice": data.lastPrice,
        "secType": data.secType
      };

      this.setPriceTypeTarget(this.priceTypeTarget);
      this.updateDenomination();

      setTimeout(() => { 
        this.isMutualFundOrder = this.symbolPrices.secType === 'Mutual Fund';

        if(this.isMutualFundOrder) {
          this.trade.expirationType = undefined;
          this.trade.expirationDate = null;
          this.isExpirationDateEnabled = false;
          this.expirationDatePlaceholder = 'n/a';
        }
      });

      this.validateTrade();
    }, () => {
      this.symbolDropdown.setValue({name: this.trade.securitySymbol}); //revert
    });
  }

  getSymbols(searchStr: any) {
    const sortByKey = this.util.sortByKey;

    this.ors.getSymbol(searchStr).subscribe((data: any) => {
      sortByKey(data, "name", "asc");
      this.symbolDropdown.set(data);
    });
  }

  setTransTypeTarget(value: any) {
    this.transTypeTarget = value;
    this.trade.transactionType = this.transTypeTarget?.value;
    this.isSellAll = this.trade.transactionType === "Sell All";
    this.isSell = this.isSellAll || this.trade.transactionType === "Sell";

    this.updateDenomination();
    this.validateTrade();
  }

  getTransTypes(searchStr: any) {
    this.transTypeDropdown.set(DropdownFilterComponent.getFilteredList(searchStr, this.transTypes));
  }

  updateDenomination() {
    if(this.symbolPrices?.secType === 'Equity') {
      this.denominations = [
        {name: "Shares", value: "Shares"}
      ];

      if(this.isSellAll) {
        this.denominationTarget = null; 
        this.trade.amountType = null;
        this.trade.quantity = null;        
      } else {
        this.denominationTarget = this.denominations[0]; 
        this.trade.amountType = "Shares";
      }

      this.trade.dollarAmount = null;
      this.isDollars = false;
    } else if(this.symbolPrices?.secType === 'Mutual Fund' && !this.isSell) {
       this.denominations = [
        {name: "Dollars", value: "Dollars"}
      ];

      this.denominationTarget = this.denominations[0]; 
      this.trade.amountType = "Dollars";
      this.trade.quantity = null;
      this.isDollars = true;
    } else {
      this.denominations = [
        {name: "Dollars", value: "Dollars"},
        {name: "Shares", value: "Shares"}
      ];

      this.denominationTarget = null; 
      this.trade.amountType = undefined;
    }

    if(this.symbolPrices?.secType === 'Mutual Fund') {
      this.expirationTypeTarget = null; 
      this.trade.expirationType = undefined;
      this.trade.expirationDate = null;
      this.isExpirationDateEnabled = false;
      this.expirationDatePlaceholder = 'n/a';
      this.trade.price = null; 
      this.trade.ordLimitPrice = null;
      this.trade.ordStopPrice = null;
      this.trade.orderType = "Market";
      this.setOrderTypeTarget({name: "Market", value: "Market"});
    }
  }

  setDenominationTarget(value: any) {
    this.denominationTarget = value;
    this.trade.amountType = this.denominationTarget?.value;
    this.isDollars = this.trade.amountType === 'Dollars';
    (this.isDollars) ? this.trade.quantity = null : this.trade.dollarAmount = null;

    this.validateTrade();
  }

  getDenominations(searchStr: any) {
    this.denominationDropdown.set(DropdownFilterComponent.getFilteredList(searchStr, this.denominations));
  }

  setOrderTypeTarget(value: any) {
    this.orderTypeTarget = value;
    this.trade.orderType = this.orderTypeTarget?.value;
    
    switch(this.trade.orderType) {
      case "Market": 
        this.expirationTypes = [
          {name: "Day", value: "DAY"},
          {name: "MOC", value: "MOC"}
        ];
        break;
      case "Limit": 
        this.expirationTypes = [
          {name: "Day", value: "DAY"},
          {name: "Day Ext", value: "DAY_EXT"},
          {name: "Ext. AM", value: "EXT_AM"},
          {name: "Ext. PM", value: "EXT_PM"},
          {name: "GT6m", value: "GT6M"},
          {name: "GTD", value: "GTD"},
          {name: "GTD Ext", value: "GTD_EXT"}
        ];
        break;
      case "Stop": 
        this.expirationTypes = [
          {name: "Day", value: "DAY"},
          {name: "GT6m", value: "GT6M"},
          {name: "GTD", value: "GTD"}
        ];
        break;
      case "Stop Limit": 
        this.expirationTypes = [
          {name: "Day", value: "DAY"},
          {name: "GT6m", value: "GT6M"},
          {name: "GTD", value: "GTD"}
        ];
        break;
    }

    this.expirationTypeTarget = null; 
    this.trade.expirationType = undefined;
    this.trade.expirationDate = null;
    this.isExpirationDateEnabled = false;
    this.expirationDatePlaceholder = 'n/a';
    this.trade.price = null; 
    this.trade.ordLimitPrice = null;
    this.trade.ordStopPrice = null;
    this.validateTrade();
  }

  getOrderTypes(searchStr: any) {
    this.orderTypeDropdown.set(DropdownFilterComponent.getFilteredList(searchStr, this.orderTypes));
  }

  setPriceTypeTarget(value: any) {
    this.trade.ordLimitPrice = null;

    setTimeout(() => {
      this.priceTypeTarget = value;
      this.trade.defaultOrderPriceType = this.priceTypeTarget?.value;

      switch(this.trade.defaultOrderPriceType) {
        case "LASTTRADE":
          this.trade.ordLimitPrice = this.symbolPrices.lastPrice;
          break;
        case "BID":
          this.trade.ordLimitPrice = this.symbolPrices.bid;
          break;
        case "ASK":
          this.trade.ordLimitPrice = this.symbolPrices.ask;
          break;
        case "MIDPOINT":
          this.trade.ordLimitPrice = this.symbolPrices.midpoint;
          break;
        default:
          this.trade.ordLimitPrice = null; 
      }

      this.validateTrade();
    });
  }

  getPriceTypes(searchStr: any) {
    this.priceTypeDropdown.set(DropdownFilterComponent.getFilteredList(searchStr, this.priceTypes));
  }

  setExpirationTypeTarget(value: any) {
    this.expirationTypeTarget = value;
    this.trade.expirationType = this.expirationTypeTarget?.value;    
    
    switch(this.trade.orderType) {
      case "Limit":
        this.isExpirationDateEnabled = this.trade.expirationType == 'GTD' || this.trade.expirationType == 'GTD_EXT';
        break;
      case "Stop":
        this.isExpirationDateEnabled = this.trade.expirationType == 'GTD';
        break;
      case "Stop Limit":
        this.isExpirationDateEnabled = this.trade.expirationType == 'GTD';
        break;
      default:
        this.isExpirationDateEnabled = false;
        this.trade.expirationDate = null;
    }

    this.trade.expirationDate = (this.trade.expirationType == 'GT6M') ? this.daySixMonthsAfter : (this.isExpirationDateEnabled) ? this.trade.expirationDate : null;
    this.expirationDatePlaceholder = (this.trade.expirationType == 'GT6M') ? this.daySixMonthsAfterDisplay : (this.isExpirationDateEnabled) ? '' : 'n/a';
    this.validateTrade();
  }

  getExpirationTypes(searchStr: any) {
    this.expirationTypeDropdown.set(DropdownFilterComponent.getFilteredList(searchStr, this.expirationTypes));
  }

  onInputChange() {
      window.setTimeout(() => {
          this.isModified = this.hasUnapprovedTrades();
          this.as.fireEvent((this.isModified) ? IrbEditorsEvents.IS_MODIFIED : IrbEditorsEvents.IS_NOT_MODIFIED, {"slot": this.slot});
          this.as.fireEvent(IrbEditorsEvents.DATA_CHANGED);
          this.showUnsavedChangesWarning = false;
          this.hasPendingFunction = false;
      });
  }

  addTrade() {
    let duplicateTrades = this.tradeList.filter((x: IOrders) => x.accountNumber == this.trade.accountNumber && x.securitySymbol == this.trade.securitySymbol);

    if(duplicateTrades.length > 0) {
      this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
        dType: 'ERROR',
        msg: "Account " + this.trade.accountNumber + " already has a trade for " + this.trade.securitySymbol,
        altMsg: "Please delete the existing trade and then add a new trade."
      } as IAlertDialogData);
      return;
    }
    
    this.trade.tradeStatus = "Unapproved";
    this.tradeList.push(this.util.clone(this.trade));
    this.grid.api.setGridOption('rowData', this.tradeList);
    this.data = [].concat(this.tradeList);
    this.onInputChange();
    this.updateGridHeight();
  }

  removeAllTrades(msg: string = "Are you sure you want to clear all the added trades?") {
    this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
      msg: msg,
      dCancelBtnLabel: "No",
      dProceedBtnLabel: "Yes",
      onProceed: () => {
        this.deleteAll();
      }
    });
  }
  
  clearTrade() {
    this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
      msg: "Are you sure you want to clear all the trade fields?",
      dCancelBtnLabel: "No",
      dProceedBtnLabel: "Yes",
      onProceed: () => {
        this.completeClearTrade();
      }
    });
  }

  completeClearTrade() {
    // clear dropdown targets
    this.accountNumberTarget = null;
    this.accountNameTarget = null;
    this.symbolTarget = null;
    this.transTypeTarget = null;
    this.denominationTarget = null;
    this.orderTypeTarget = null;
    this.priceTypeTarget = null;
    this.expirationTypeTarget = null;
    
    //clear trade
    this.trade = {};
    this.trade.orderType = null;
    this.trade.quantity = null;
    this.trade.dollarAmount = null;
    this.trade.price = null;
    this.trade.ordLimitPrice = null;
    this.trade.ordStopPrice = null;
    this.trade.expirationDate = null;

    // clear booleans
    this.isMutualFundOrder = false;
    this.isDollars = false;
    this.isExpirationDateEnabled = false;
    this.isSell = false;
    this.isSellAll = false;
    this.expirationDatePlaceholder = 'n/a';

    this.validateTrade();

    setTimeout(() => {
      this.accountNumberDropdown?.inputControl.markAsUntouched();
      this.accountNumberDropdown?.inputControl.updateValueAndValidity();
      this.accountNameDropdown?.inputControl.markAsUntouched();
      this.accountNameDropdown?.inputControl.updateValueAndValidity();
      this.symbolDropdown?.inputControl.markAsUntouched();
      this.symbolDropdown?.inputControl.updateValueAndValidity();
      this.transTypeDropdown?.inputControl.markAsUntouched();
      this.transTypeDropdown?.inputControl.updateValueAndValidity();
      this.denominationDropdown?.inputControl.markAsUntouched();
      this.denominationDropdown?.inputControl.updateValueAndValidity();
      this.orderTypeDropdown?.inputControl.markAsUntouched();
      this.orderTypeDropdown?.inputControl.updateValueAndValidity();
      this.priceTypeDropdown?.inputControl.markAsUntouched();
      this.priceTypeDropdown?.inputControl.updateValueAndValidity();
      this.expirationTypeDropdown?.inputControl.markAsUntouched();
      this.expirationTypeDropdown?.inputControl.updateValueAndValidity();
      this.quantityInput?.inputControl.markAsUntouched();
      this.quantityInput?.inputControl.updateValueAndValidity();
      this.dollarAmountInput?.inputControl.markAsUntouched();
      this.dollarAmountInput?.inputControl.updateValueAndValidity();
      this.priceInput?.inputControl.markAsUntouched();
      this.priceInput?.inputControl.updateValueAndValidity();
      this.limitPriceInput?.inputControl.markAsUntouched();
      this.limitPriceInput?.inputControl.updateValueAndValidity();
      this.stopPriceInput?.inputControl.markAsUntouched();
      this.stopPriceInput?.inputControl.updateValueAndValidity();
      this.expirationDate?.control.markAsUntouched();
      this.expirationDate?.control.updateValueAndValidity();
    });
  }

  validateTrade() {    
    this.isTradeValid = true;
    this.isTradeValid = this.isTradeValid && typeof this.trade.accountNumber != "undefined";
    this.isTradeValid = this.isTradeValid && typeof this.trade.securitySymbol != "undefined";    
    this.isTradeValid = this.isTradeValid && typeof this.trade.transactionType != "undefined";    
    this.isTradeValid = this.isTradeValid && ((typeof this.trade.amountType != "undefined" && this.trade.amountType != null) || this.isSellAll);        
    this.isTradeValid = this.isTradeValid && ((typeof this.trade.quantity != "undefined" && this.trade.quantity != null && !this.quantityInput?.inputControl.invalid) || this.isSellAll || this.isDollars);    
    this.isTradeValid = this.isTradeValid && ((typeof this.trade.dollarAmount != "undefined" && this.trade.dollarAmount != null && !this.dollarAmountInput?.inputControl.invalid) || this.isSellAll || !this.isDollars);    
    this.isTradeValid = this.isTradeValid && ((typeof this.trade.orderType != "undefined") || this.isMutualFundOrder);         
    this.isTradeValid = this.isTradeValid && ((typeof this.trade.expirationType != "undefined") || this.isMutualFundOrder); 

    if(this.trade.orderType == 'Limit') {
      this.isTradeValid = this.isTradeValid && typeof this.trade.defaultOrderPriceType != "undefined";  
      this.isTradeValid = this.isTradeValid && (typeof this.trade.ordLimitPrice != "undefined" && !this.limitPriceInput?.inputControl.invalid) && this.trade.ordLimitPrice != null;  
    } else if(this.trade.orderType == 'Stop') {
      this.isTradeValid = this.isTradeValid && (typeof this.trade.ordStopPrice != "undefined" && !this.stopPriceInput?.inputControl.invalid) && this.trade.ordStopPrice != null;
    } else if(this.trade.orderType == 'Stop Limit') {
      this.isTradeValid = this.isTradeValid && (typeof this.trade.ordLimitPrice != "undefined" && !this.limitPriceInput?.inputControl.invalid) && this.trade.ordLimitPrice != null; 
      this.isTradeValid = this.isTradeValid && (typeof this.trade.ordStopPrice != "undefined" && !this.stopPriceInput?.inputControl.invalid) && this.trade.ordStopPrice != null;  
    }

    setTimeout(() => {
      this.isTradeValid = this.isTradeValid && ((this.isExpirationDateEnabled && !this.expirationDate.invalid) || !this.isExpirationDateEnabled);
    });
  }

  save() {
    this.as.fireEvent(IrbEditorsEvents.SAVE_START, {"slot": this.slot});
    let unapprovedTrades = this.tradeList.filter((x: IOrders) => x.tradeStatus == 'Unapproved');

    this.ors.validateOrders(unapprovedTrades).subscribe((data: any) => {
        const statusId = data?.responseUtilityMap?.statusId;

        if(statusId) {
            this.validateOrdersInterval(statusId);
        } else {
            this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
        }
    }, (_err: HttpErrorResponse) => {
        this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
    });
  }

  validateOrdersInterval(statusId: number) {
    this.ors.validateOrdersStatus(statusId).subscribe((resp: any) => {
      if (resp.responseStatus === "SUCCESS") {
        const status = resp.responseUtilityMap?.reqStatus?.trim().toUpperCase() || 'FAILED';

        if (status === 'STARTED' || status === 'NEW' || status === 'IN_PROGRESS') {
          setTimeout(() => { this.validateOrdersInterval(statusId); }, (this.isUiTest) ? 0 : 3000);
        } else if (status === 'COMPLETED') {
          this.validateOrdersFinish(resp.responseData);
        } else {
          this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
        }
      } else {
          this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
      }
    }, () => {
        this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
    });
  }

  validateOrdersFinish(resp: any) {
    const isValid = (resp.validRecords?.length > 0);
    const isInvalid = (resp.invalidRecords?.length > 0);
    const hasActiveOrders = (resp.activeOrderAcctNumSet?.length > 0);
    const hasDuplicateOrders = resp.isDuplicateRecordExists;

    if(isInvalid || hasActiveOrders || hasDuplicateOrders) {
      this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, {"slot": this.slot});
      this.as.fireEvent(IrbDialogsEvents.SHOW_QOE_RESULT_DIALOG, {
        data: resp,
        onProceed: (resp: any) => { 
          this.as.fireEvent(IrbEditorsEvents.SAVE_START, {"slot": this.slot});
          this.insertOrders(resp); 
        }
      });
    } else if(isValid) {
      this.insertOrders(resp);
    } else {
      this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
    }
  }

  insertOrders(resp: any) {
    this.ors.insertOrders(resp.validRecords).subscribe((data: any) => {
        const statusId = data?.responseUtilityMap?.statusId;

        if(statusId) {
            this.insertOrdersInterval(statusId);
        } else {
            this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
        }
    }, (_err: HttpErrorResponse) => {
        this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
    });
  }

  insertOrdersInterval(statusId: number) {
    this.ors.insertOrdersStatus(statusId).subscribe((resp: any) => {
      if (resp.responseStatus === "SUCCESS") {
        const status = resp.responseUtilityMap?.reqStatus?.trim().toUpperCase() || 'FAILED';

        if (status === 'STARTED' || status === 'NEW' || status === 'IN_PROGRESS') {
          setTimeout(() => { this.insertOrdersInterval(statusId); }, (this.isUiTest) ? 0 : 3000);
        } else if (status === 'COMPLETED') {
          this.insertOrdersFinish();
        } else {
          this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
        }
      } else {
          this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
      }
    }, () => {
        this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
    });
  }

  insertOrdersFinish() {
    this.tradeList.filter((x: IOrders) => x.tradeStatus == 'Unapproved').forEach((x: IOrders) => x.tradeStatus = 'Approved to Order Blotter');
    this.grid.api.setGridOption('rowData', this.tradeList);
    this.onInputChange();

    this.as.fireEvent(IrbEditorsEvents.SAVE_SUCCESS, {
        "hasPendingFunction": this.hasPendingFunction,
        "data": "Order Blotter updated.",
        "slot": this.slot
    });

    this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, {"slot": this.slot});
    this.as.fireEvent(IrbAppEvents.GRID_REFRESH, {"slot": this.slot});
  }

  deleteAll() {
    this.tradeList = [];
    this.grid.api.setGridOption('rowData', this.tradeList);
    this.data = [];
    this.onInputChange();
    this.updateGridHeight();
  }

  delete(t: IOrders) {
    for (let i = 0; i < this.tradeList.length; i++) {
      if (this.tradeList[i].accountNumber === t.accountNumber && this.tradeList[i].securitySymbol === t.securitySymbol) {
        this.tradeList.splice(i--,1);
        break;
      }
    }

    this.grid.api.setGridOption('rowData', this.tradeList);
    this.data = [].concat(this.tradeList);
    this.onInputChange();
    this.updateGridHeight();
  }

  cancelDelete() {
    this.ctas.remove();
  }

  getRowCount() {
    return this.tradeList?.length || 0;
  }

  updateGridHeight() {
    if(this.tradeList.length <= 6) {
      this.gridHeight = "240px";
    } else {
      this.gridHeight = "480px";
    }

    setTimeout(() => this.onResize());
  }

  hasUnapprovedTrades() {
    const a = this.tradeList.filter((x: IOrders) => x.tradeStatus == 'Unapproved');
    return a.length > 0;
  }
}
